import { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { Button } from './ui/button';
import { X, Loader2, FolderKanban } from 'lucide-react';
import { toast } from 'sonner';
import { StepProject } from './AddToCrm/StepProject';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
  TaskProjectLinkModal — "Add to Project" / "Change Project" button on the
  Task Details modal (NotificationBars.jsx). Reuses the same StepProject
  search/create-project UI EditCrmQuoteModal uses, but only the project
  step (a Task doesn't need a Contractor/Sub-Contractor Package) — writes
  the result via PATCH /zoho/tasks/{task_id}/project.
*/
export default function TaskProjectLinkModal({ isOpen, onClose, taskId, currentProject, onLinked }) {
  const [projectQuery, setProjectQuery] = useState(currentProject?.project_name || '');
  const [projectLoading, setProjectLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(
    currentProject?.project_id ? { id: currentProject.project_id, deal_name: currentProject.project_name } : null
  );
  const [showProjectCreate, setShowProjectCreate] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectBarbourId, setNewProjectBarbourId] = useState('');
  const [newProjectPostcode, setNewProjectPostcode] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [saving, setSaving] = useState(false);

  const runProjectSearch = async (q) => {
    if (!q || q.trim().length < 2) return setProjects([]);
    setProjectLoading(true);
    try {
      const res = await api.get(`${API}/zoho/search-projects`, { params: { q } });
      setProjects(res.data.projects || []);
    } catch { setProjects([]); } finally { setProjectLoading(false); }
  };

  const createProject = async () => {
    if (!newProjectName.trim()) return toast.error('Project name is required');
    setCreatingProject(true);
    try {
      const res = await api.post(`${API}/zoho/create-project`, {
        deal_name: newProjectName.trim(),
        barbour_project_id: newProjectBarbourId.trim() || null,
        account_id: null,
        postcode: newProjectPostcode.trim() || null,
      });
      setSelectedProject({ id: res.data.id, deal_name: res.data.deal_name });
      setShowProjectCreate(false);
      toast.success('Project created in Zoho CRM');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create project');
    } finally { setCreatingProject(false); }
  };

  const linkProject = async () => {
    if (!selectedProject?.id) return toast.error('Pick or create a project first');
    setSaving(true);
    try {
      const res = await api.patch(`${API}/zoho/tasks/${taskId}/project`, { project_id: selectedProject.id });
      toast.success('Task linked to project');
      onLinked?.(res.data?.link);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link task to project');
    } finally { setSaving(false); }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10003] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="task-project-link-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto border border-gray-200"
        onClick={(e) => e.stopPropagation()}
        data-testid="task-project-link-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FolderKanban className="w-5 h-5 text-orange-500" />
            <h2 className="text-base font-bold text-gray-900">
              {currentProject?.project_id ? 'Change Linked Project' : 'Add to Project'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="task-project-link-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <StepProject
            step={1} mode="project" showAccessRequest={false}
            projectQuery={projectQuery} setProjectQuery={setProjectQuery}
            projectLoading={projectLoading} projects={projects}
            selectedProject={selectedProject} setSelectedProject={setSelectedProject}
            showProjectCreate={showProjectCreate} setShowProjectCreate={setShowProjectCreate}
            newProjectName={newProjectName} setNewProjectName={setNewProjectName}
            newProjectBarbourId={newProjectBarbourId} setNewProjectBarbourId={setNewProjectBarbourId}
            newProjectPostcode={newProjectPostcode} setNewProjectPostcode={setNewProjectPostcode}
            creatingProject={creatingProject} runProjectSearch={runProjectSearch}
            createProject={createProject} quoteForm={null}
            setStep={() => {}}
            onProjectsUpdate={() => {}}
          />

          <div className="flex justify-between pt-2 border-t border-gray-100">
            <Button variant="ghost" onClick={onClose} data-testid="task-project-link-cancel-btn">Cancel</Button>
            <Button
              onClick={linkProject}
              disabled={saving || !selectedProject?.id}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="task-project-link-save-btn"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Linking…</> : 'Link Task to This Project'}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
